/**
 * Blog cover images — Wave 172.
 * Deterministic category → realistic documentary photo mapping so every
 * article (TH + EN) has a real hero image, a contextual TH/EN alt text and a
 * unique og:image / Article.image for Google Discover + AI extraction.
 *
 * Files live in public/blog/ (1200×630, JPEG).
 */

const SITE = "https://nyclanguageinstitute.com";

export interface BlogCover {
  /** site-root relative path, e.g. /blog/cover-notary.jpg */
  src: string;
  /** absolute https URL for og:image / JSON-LD */
  absolute: string;
  altTh: string;
  altEn: string;
  width: number;
  height: number;
}

type CoverSeed = { file: string; altTh: string; altEn: string };

const COVERS: Record<string, CoverSeed> = {
  Notary: {
    file: "cover-notary.jpg",
    altTh: "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) กำลังประทับตรารับรองเอกสารในสำนักงานกฎหมายที่กรุงเทพฯ",
    altEn: "Notarial services attorney in a Bangkok law office pressing a notarial seal onto certified documents",
  },
  Translation: {
    file: "cover-translation.jpg",
    altTh: "โต๊ะทำงานนักแปลเอกสารรับรอง มีเอกสารสองภาษาไทย-อังกฤษ พจนานุกรมและโน้ตบุ๊กสำหรับงานแปลรับรอง NAATI และแปลกฎหมาย",
    altEn: "Certified translator's desk with bilingual Thai–English documents, dictionary and laptop used for NAATI and legal translation work",
  },
  Visa: {
    file: "cover-visa.jpg",
    altTh: "ผู้ยื่นคำขอวีซ่าถือหนังสือเดินทางพร้อมชุดเอกสารประกอบบนเคาน์เตอร์สถานทูต",
    altEn: "Visa applicant holding a passport with a supporting document set on an embassy counter",
  },
  Legalization: {
    file: "cover-legalization.jpg",
    altTh: "ชุดเอกสารรับรองมัดริบบิ้นพร้อมตราประทับ รอยื่นรับรองที่กรมการกงสุล / Apostille",
    altEn: "Ribbon-bound certified document set with an official seal awaiting consular legalisation or Apostille",
  },
  Legal: {
    file: "cover-legal.jpg",
    altTh: "ที่ปรึกษากฎหมายของ NYC Language Institute ให้คำปรึกษาลูกค้าแบบตัวต่อตัวในห้องประชุมสำนักงาน",
    altEn: "NYC Language Institute legal consultant advising a client one-on-one in the office meeting room",
  },
};

const FALLBACK = COVERS.Legal;

/** Resolve the cover for an article. Category-driven, SSR-safe, no I/O. */
export function blogCover(category: string | undefined, locale: "th" | "en" = "th"): BlogCover {
  const seed = (category && COVERS[category]) || FALLBACK;
  const src = `/blog/${seed.file}`;
  return {
    src,
    absolute: `${SITE}${src}`,
    altTh: seed.altTh,
    altEn: seed.altEn,
    width: 1200,
    height: 630,
    ...(locale ? {} : {}),
  };
}

/** Convenience: alt text in the requested locale. */
export function blogCoverAlt(category: string | undefined, locale: "th" | "en"): string {
  const c = blogCover(category, locale);
  return locale === "en" ? c.altEn : c.altTh;
}
